import { useEffect, useId, useRef, useState } from "react";

import { Input, Label } from "@bigbinary/neeto-atoms";
import { Search as SearchIcon } from "lucide-react";
import useFuncDebounce from "neetocommons/react-utils/useFuncDebounce";
import useQueryParams from "neetocommons/react-utils/useQueryParams";
import useUpdateEffect from "neetocommons/react-utils/useUpdateEffect";
import { buildUrl } from "neetocommons/utils/general";
import PropTypes from "prop-types";
import { mergeLeft, omit } from "ramda";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { getInitialSearchValue } from "./utils";

const Search = ({
  value,
  debounceTime = 300,
  onSearch,
  onChange,
  enableUrlSync = false,
  searchParamName = "search_term",
  className,
  label,
  ...inputProps
}) => {
  const { t } = useTranslation();
  const queryParams = useQueryParams({ toCamelCase: false });
  const history = useHistory();
  const inputId = useId();

  const [internalValue, setInternalValue] = useState(
    getInitialSearchValue({
      initialValue: value,
      enableUrlSync,
      searchParamName,
    })
  );
  const trimmedValue = value?.trim();

  useUpdateEffect(() => {
    if (!enableUrlSync) return;

    const searchParamValue = queryParams?.[searchParamName] ?? "";
    setInternalValue(searchParamValue);
  }, [queryParams?.[searchParamName]]);

  const handleSearchQueryParams = trimmedValue => {
    if (!trimmedValue && !queryParams?.[searchParamName]) return;

    if (trimmedValue === queryParams?.[searchParamName]) return;

    const pathname = history.location.pathname;

    if (trimmedValue) {
      history.push(
        buildUrl(
          pathname,
          mergeLeft({ [searchParamName]: trimmedValue }, queryParams)
        )
      );
    } else {
      history.push(buildUrl(pathname, omit([searchParamName], queryParams)));
    }
  };

  const handleDebouncedSearchTermChange = useFuncDebounce(trimmedValue => {
    if (onSearch) onSearch(trimmedValue);
    else if (enableUrlSync) handleSearchQueryParams(trimmedValue);
  }, debounceTime);

  useUpdateEffect(() => {
    handleDebouncedSearchTermChange(trimmedValue);
  }, [trimmedValue]);

  const prevValueRef = useRef(value);
  if (value !== undefined && value !== prevValueRef.current) {
    setInternalValue(value);
    prevValueRef.current = value;
  }

  const handleOnChange = e => {
    const trimmedValue = e.target.value?.trim();
    onChange?.(e);
    setInternalValue(e.target.value);

    if (internalValue?.trim() !== trimmedValue) {
      handleDebouncedSearchTermChange(trimmedValue);
    }
  };

  useEffect(() => handleDebouncedSearchTermChange.cancel, []);

  return (
    <div {...{ className }}>
      {label && (
        <Label className="mb-1" htmlFor={inputId}>
          {label}
        </Label>
      )}
      <div className="relative">
        <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          className="pl-9 [&::-webkit-search-cancel-button:hover]:cursor-pointer [&::-webkit-search-decoration:hover]:cursor-pointer"
          id={inputId}
          placeholder={t("neetoMolecules.common.actions.search")}
          type="search"
          value={internalValue}
          onChange={handleOnChange}
          {...inputProps}
        />
      </div>
    </div>
  );
};

Search.propTypes = {
  value: PropTypes.string,
  debounceTime: PropTypes.number,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  enableUrlSync: PropTypes.bool,
  searchParamName: PropTypes.string,
  inputProps: PropTypes.object,
};

export default Search;
